/**
 * Notifications Hook
 *
 * Fetches and manages in-app notifications from the care API.
 * These notifications appear in the bell icon popover in the TopBar.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../api/louise-api";
import { throwApiError } from "../api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import {
  fetchDevNotificationFixtures,
  getDevUnreadNotificationCount,
  getNotificationFixtureMode,
  markAllDevNotificationsRead,
  markDevNotificationRead,
} from "../notifications/notification-fixtures";

// ============================================================================
// Types
// ============================================================================

/** Notification as returned from the canonical GET /me/notifications endpoint. */
export type Notification = ResponseBody<
  paths["/me/notifications"]["get"]
>["data"][number];

export type NotificationType = Notification["type"];

// ============================================================================
// Query Keys
// ============================================================================

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (tenantId: string, patientId: string, fixtureMode?: string | null) =>
    [
      ...notificationKeys.all,
      "list",
      tenantId,
      patientId,
      fixtureMode ?? "api",
    ] as const,
  unreadCount: (
    tenantId: string,
    patientId: string,
    fixtureMode?: string | null,
  ) =>
    [
      ...notificationKeys.all,
      "unreadCount",
      tenantId,
      patientId,
      fixtureMode ?? "api",
    ] as const,
};

// ============================================================================
// API Fetchers
// ============================================================================

function getTimestamp(value: string) {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortNotificationsNewestFirst(notifications: Notification[]) {
  return [...notifications].sort((a, b) => {
    const byDate = getTimestamp(b.createdAt) - getTimestamp(a.createdAt);
    if (byDate !== 0) return byDate;
    return b.id.localeCompare(a.id);
  });
}

function filterExpiredNotifications(notifications: Notification[]) {
  return notifications.filter((notification) => {
    if (!notification.expiresAt) return true;
    const expiresAt = Date.parse(notification.expiresAt);
    return Number.isNaN(expiresAt) || expiresAt > Date.now();
  });
}

async function fetchNotifications(
  client: ReturnType<typeof useApiClient>["client"],
): Promise<Notification[]> {
  const response = await client.GET("/me/notifications");

  if (response.error) {
    throwApiError(
      response.error,
      "Failed to fetch notifications",
      "fetchNotifications",
      response.response?.status,
    );
  }

  return sortNotificationsNewestFirst(
    filterExpiredNotifications(response.data?.data ?? []),
  );
}

async function fetchUnreadCount(
  client: ReturnType<typeof useApiClient>["client"],
): Promise<number> {
  const response = await client.GET("/me/notifications/unread/count");

  if (response.error) {
    throwApiError(
      response.error,
      "Failed to fetch unread notifications",
      "fetchUnreadCount",
      response.response?.status,
    );
  }

  return response.data?.data?.count ?? 0;
}

// ============================================================================
// Hooks
// ============================================================================

export interface UseNotificationsOptions {
  /** Whether to enable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch notifications.
 */
export function useNotifications(options: UseNotificationsOptions = {}) {
  const { enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();
  const fixtureMode = getNotificationFixtureMode();

  return useQuery({
    queryKey: notificationKeys.list(
      tenantId ?? "",
      patientId ?? "",
      fixtureMode,
    ),
    queryFn: () =>
      fixtureMode
        ? fetchDevNotificationFixtures(fixtureMode)
        : fetchNotifications(client),
    enabled: fixtureMode != null || (enabled && !!tenantId && !!patientId),
    staleTime: 1000 * 60 * 2, // 2 minutes
    // Keep background notification traffic conservative until the API's
    // capacity for badge-event polling is confirmed. Events remain available
    // in the notification center when the next sync completes.
    refetchInterval: 1000 * 60 * 5,
  });
}

/**
 * Hook to get unread notification count (for badge display).
 */
export function useUnreadNotificationCount(
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();
  const fixtureMode = getNotificationFixtureMode();

  return useQuery({
    queryKey: notificationKeys.unreadCount(
      tenantId ?? "",
      patientId ?? "",
      fixtureMode,
    ),
    queryFn: () =>
      fixtureMode
        ? Promise.resolve(getDevUnreadNotificationCount(fixtureMode))
        : fetchUnreadCount(client),
    enabled: fixtureMode != null || (enabled && !!tenantId && !!patientId),
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes
  });
}

/**
 * Hook to mark a notification as read.
 */
export function useMarkNotificationRead() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const fixtureMode = getNotificationFixtureMode();
  const listKey = notificationKeys.list(
    tenantId ?? "",
    patientId ?? "",
    fixtureMode,
  );
  const unreadKey = notificationKeys.unreadCount(
    tenantId ?? "",
    patientId ?? "",
    fixtureMode,
  );

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (fixtureMode) {
        return markDevNotificationRead(fixtureMode, notificationId);
      }
      if (!tenantId) {
        throw new Error("No tenant ID");
      }

      const response = await client.PATCH(
        "/{tenantId}/me/notifications/{id}/read",
        {
          params: {
            path: { tenantId, id: notificationId },
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to mark notification as read",
          "useMarkNotificationRead",
          response.response?.status,
        );
      }

      return response.data;
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previousNotifications =
        queryClient.getQueryData<Notification[]>(listKey);
      const previousUnreadCount = queryClient.getQueryData<number>(unreadKey);
      const wasUnread = previousNotifications?.some(
        (notification) =>
          notification.id === notificationId && notification.readAt === null,
      );

      queryClient.setQueryData<Notification[]>(
        listKey,
        (notifications) =>
          notifications?.map((notification) =>
            notification.id === notificationId
              ? {
                  ...notification,
                  readAt: notification.readAt ?? new Date().toISOString(),
                }
              : notification,
          ) ?? notifications,
      );
      if (previousUnreadCount !== undefined && wasUnread) {
        queryClient.setQueryData(
          unreadKey,
          Math.max(0, previousUnreadCount - 1),
        );
      }

      return { previousNotifications, previousUnreadCount };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(listKey, context.previousNotifications);
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(unreadKey, context.previousUnreadCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Hook to mark all notifications as read.
 */
export function useMarkAllNotificationsRead() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const fixtureMode = getNotificationFixtureMode();
  const listKey = notificationKeys.list(
    tenantId ?? "",
    patientId ?? "",
    fixtureMode,
  );
  const unreadKey = notificationKeys.unreadCount(
    tenantId ?? "",
    patientId ?? "",
    fixtureMode,
  );

  return useMutation({
    mutationFn: async () => {
      if (fixtureMode) {
        return { data: { count: markAllDevNotificationsRead(fixtureMode) } };
      }
      if (!tenantId) {
        throw new Error("No tenant ID");
      }

      const response = await client.POST("/me/notifications/read");

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to mark all notifications as read",
          "useMarkAllNotificationsRead",
          response.response?.status,
        );
      }

      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previousNotifications =
        queryClient.getQueryData<Notification[]>(listKey);
      const previousUnreadCount = queryClient.getQueryData<number>(unreadKey);
      const now = new Date().toISOString();

      queryClient.setQueryData<Notification[]>(
        listKey,
        (notifications) =>
          notifications?.map((notification) => ({
            ...notification,
            readAt: notification.readAt ?? now,
          })) ?? notifications,
      );
      queryClient.setQueryData(unreadKey, 0);

      return { previousNotifications, previousUnreadCount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(listKey, context.previousNotifications);
      }
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(unreadKey, context.previousUnreadCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
